import { ColDef } from 'ag-grid-community';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomerSegment } from '@callmart/types';
import { useCreateWorktype, useUpdateWorktype, useWorktypes } from '../hooks';
import CRUDSettings from '../components/General/CRUDSettings';
import { Worktype } from '../types/Worktype.interface';
import { useForecasters } from '../hooks/api/forecasters';
import { useCustomerSegments } from '../hooks/api/forecasts';
import { Forecaster } from '../types/Forecaster.interface';
import WorktypeDialog from '../components/Worktypes/WorktypeDialog';
import { ColorizedNameRenderer } from '../utils/agGridUtils';

const Worktypes = () => {
    const { t } = useTranslation();
    const [selectedWorktype, setSelectedWorktype] = useState<Worktype | undefined>(undefined);
    const [showDialog, setShowDialog] = useState(false);
    const [worktypes, isLoading] = useWorktypes();
    const { data: forecasters } = useForecasters();
    const { data: customerSegments } = useCustomerSegments();
    const { mutate: handleCreateWorktype } = useCreateWorktype({
        onSettled: (data, error) => {
            if (!error) setShowDialog(false);
        },
    });
    const { mutate: handleUpdateWorktype } = useUpdateWorktype({
        onSettled: (data, error) => {
            if (!error) setShowDialog(false);
        },
    });

    const handleDialogSubmit = useCallback(
        (data: { worktype: string; shortName?: string; forecasterIds: number[]; customerSegmentIds: number[] }) => {
            if (selectedWorktype) {
                handleUpdateWorktype({
                    id: selectedWorktype.id,
                    worktype: data.worktype,
                    shortName: data.shortName || null,
                    forecasters: data.forecasterIds,
                    customerSegments: data.customerSegmentIds,
                });
            } else {
                handleCreateWorktype({
                    worktype: data.worktype,
                    shortName: data.shortName || null,
                    forecasters: data.forecasterIds,
                    customerSegments: data.customerSegmentIds,
                });
            }
        },
        [handleCreateWorktype, handleUpdateWorktype, selectedWorktype]
    );

    const columnDefs: ColDef[] = [
        {
            field: 'worktype',
            headerName: t('field.worktype'),
            editable: (params) => params.data.isRowEdited || params.data.isNewRecord,
        },
        {
            field: 'shortName',
            headerName: t('field.shortName'),
            editable: (params) => params.data.isRowEdited || params.data.isNewRecord,
            cellEditorParams: { maxLength: 23 },
        },
        {
            field: 'forecasters',
            headerName: t('field.forecasters'),
            valueGetter: ({ data }) => (data.forecasters || []).map((forecaster: Forecaster) => forecaster.name),
            cellRenderer: ColorizedNameRenderer,
        },
        {
            field: 'customerSegments',
            headerName: t('segment.label'),
            valueGetter: ({ data }) => (data.customerSegments || []).map((segment: CustomerSegment) => segment.label),
        },
    ];

    return (
        <>
            <CRUDSettings
                tableRows={worktypes}
                columnDefs={columnDefs}
                btnText={t('worktype.addNew')}
                createRecord={handleCreateWorktype}
                updateRecord={handleUpdateWorktype}
                recordType="Worktype"
                isCreationViaDialog={true}
                isEditViaDialog={true}
                setShowCreationDialog={setShowDialog}
                loading={isLoading}
                onSelectedRowChange={setSelectedWorktype}
            />
            {showDialog && (
                <WorktypeDialog
                    worktype={selectedWorktype}
                    showDialog={showDialog}
                    forecasters={forecasters}
                    customerSegments={customerSegments}
                    onSubmit={handleDialogSubmit}
                    isLoading={false}
                    onClose={() => setShowDialog(false)}
                />
            )}
        </>
    );
};

export default Worktypes;
